import { PageSectionProps } from "@components/page-section/page-section";
import { ExpiringCredits } from "@custom-types/credit-types";
import { Box } from "@mui/material";
import { AddCredits } from "@pages/settings/subscription-settings/credits/add-credits";
import { CreditSubscriptions } from "@pages/settings/subscription-settings/credits/credit-subscriptions";
import { CreditsBalanceItem } from "@pages/settings/subscription-settings/credits/credits-balance-item";
import { SubscriptionExpirationAlert } from "@pages/settings/subscription-settings/subscription-expiration-alert";
import {
  CREDIT_DISPLAY_NAME,
  CREDITS_DISPLAY_NAME,
} from "@src/constants/credit-constants";
import {
  allCreditSubscriptionsSelector,
  companyCreditBalanceSelector,
  getEarliestExpiringCreditsSelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { nounPluralize } from "@utils/data-display";
import { isCreditsNearExpiry } from "@utils/sdb-company-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { useMemo } from "react";

interface UseCreditSettings {
  /** Props for the credit setting section */
  creditsSectionProps: PageSectionProps[];
}

/** Custom hook to return credit settings props for subscription page */
export function useCreditSettings(): UseCreditSettings {
  const creditSubscriptions = useAppSelector(allCreditSubscriptionsSelector);
  const companyCreditBalance: number = useAppSelector(
    companyCreditBalanceSelector
  );
  const earliestExpiringCredits: ExpiringCredits | undefined = useAppSelector(
    getEarliestExpiringCreditsSelector
  );
  const isExpiringSoon = isCreditsNearExpiry(earliestExpiringCredits);

  // Whether the section to display existing token balance should show
  // Show if the company has or ever had tokens, regardless of the floorplan feature
  const shouldShowExistingTokensSection = creditSubscriptions.length > 0;

  // Label for the credit section based on the available credit and expiration date.
  const subscriptionCreditLabel = useMemo(() => {
    if (earliestExpiringCredits) {
      return `${nounPluralize({
        shouldShowCounter: true,
        counter: earliestExpiringCredits.totalCredits,
        word: CREDIT_DISPLAY_NAME,
      })} expire on`;
    }
    return `No ${CREDITS_DISPLAY_NAME} available`;
  }, [earliestExpiringCredits]);

  // Props for the credits section
  const creditsSectionProps: PageSectionProps[] = useMemo(() => {
    return [
      {
        title: capitalizeFirstLetter(CREDITS_DISPLAY_NAME),
        subtitle: `Use and share ${CREDITS_DISPLAY_NAME} with your team to generate floor plans within your projects. 
        Explore the different ${CREDIT_DISPLAY_NAME} bundles we offer.`,
        isHidden: !shouldShowExistingTokensSection,
        footerAction: <AddCredits />,
        items: [
          {
            label: "Balance",
            content: <CreditsBalanceItem creditBalance={companyCreditBalance} />,
          },
          {
            label: subscriptionCreditLabel,
            content: (
              <CreditSubscriptions
                earliestExpiringCredits={earliestExpiringCredits}
                isExpiringSoon={isExpiringSoon}
              />
            ),
            shouldHideBottomBorder: isExpiringSoon,
          },
          {
            content: (
              <Box sx={{ height: "70px", width: "100%" }}>
                <SubscriptionExpirationAlert
                  description={`Your ${CREDITS_DISPLAY_NAME} are expiring soon! Use them before they expire, or you'll lose them.`}
                />
              </Box>
            ),
            shouldHideBottomBorder: true,
            shouldContentTakeFullWidth: true,
            isHidden: !isExpiringSoon,
          },
        ],
      },
    ];
  }, [
    companyCreditBalance,
    earliestExpiringCredits,
    isExpiringSoon,
    shouldShowExistingTokensSection,
    subscriptionCreditLabel,
  ]);

  return { creditsSectionProps };
}
